import binary_bits_grid from '../tech-images/binary_bits_grid.png'
import llama_q2 from '../tech-images/llama_q2.png'
import llama_q8 from '../tech-images/llama_q8.png'

const QuantizationInLLMsContent = {
    title : "Understanding Quantization in Large Language Models",
    paragraphs : [
    {   heading: 'Introduction', 
        content: (
            <p>
                Before we start, it’s important to clarify a few basics. LLM families are defined by their underlying architecture. Each organization designs its own model structure. Examples include GPT, LLaMA, Claude, DeepSeek, etc. A specific LLM refers to a particular version or checkpoint of that family, such as GPT‑3.5 or LLaMA 3.2.
                <br/>
                <br/>
                Every LLM consists of two components: the model architecture and the model weights. The architecture defines how the neural network layers connect and interact. The weights are the numeric parameter values the model learns during training. These weights are often high precision values, containing many decimal places.
            </p>
        )
    },
    {   heading: 'Why Precision Matters?', 
        content: (
            <p>
                Since weights are stored in binary, higher precision means more bits per value, which increases memory usage. Combined with a large architecture, these high‑precision weight values significantly contribute to a model’s overall size.
                <br/>
                <br/>
                To reduce the size of an LLM, you can either simplify the architecture or reduce the precision of the weights. For example, instead of storing a value like 12.348235347468, you could store 12.3482. This requires fewer bits in memory, reducing the overall model size.
                <br/>
                <br/>
                But quantization is not as simple as rounding off weights!
            </p>
        )
    },
    {
        heading:'Binary Representation Refresher',
        content: ( <>
                <p>
                    Before diving deeper, consider how numbers are represented in different bases. For example, representation of the decimal value 123 in base 2 will be like:
                </p>
                <img
                    src={binary_bits_grid}
                    alt="Binary representation example"
                    className="my-4 mx-auto"
                    loading="lazy"
                    width="1979"
                    height="416"
                />
                <p>
                    Likewise, 8 bits can represent 256 distinct numbers either from 0 to 255 or from −128 to 127 if one bit is used for +/- sign.
                    <br/>
                    <br/>
                    This limitation on representable values is at the core of quantization.
                </p>
            </>
        )
    },
    {
        heading:'How Quantization Works? - A simplified view',
        content:(
            <>
                <p>
                    Quantization uses a scaling factor. Suppose the scaling factor is s = 0.001. A weight such as 12.348235347468 becomes:
                    <br/>
                    <br/>
                    12.348235347468 / 0.001 = 12348.235347468 ≈ 12348
                    <br/>
                    <br/>
                    As the final quantized integer value can be stored in INT14, it requires far fewer bits than the original high‑precision floating‑point number. This drastically reduces the model’s memory footprint.
                </p>
            </>
        )
    },
    {
        heading:'The Trade‑off',
        content:(
            <>
                <p>
                    The reduction in precision comes at a cost. When fewer bits are used to represent each weight, small differences between weight values are lost. As an analogy, we can say for example, 12.345567 and 12.345589 both map to 12345 under the scaling factor. Since these two distinct values collapse into one, the outputs of their respective neurons become identical.
                    <br/>
                    <br/>
                    Quantization compresses a model by mapping many floating-point weights to a small set of discrete integer values. Multiple distinct floating-point weights often collapse into the same quantized bucket. When that happens, the model can no longer represent the granular differences. This loss of granularity can reduce model accuracy. Quantization makes LLMs smaller and faster but slightly less precise.
                </p>
            </>
        )
    },
    {
        heading:'An Example of Reduced Accuracy',
        content:(
            <>
                <p>
                    To show the practical impact, both LLaMa3.2:1b-instruct-q2_k and LLaMa3.2:1b-instruct-q8_0 models were used for extracting structured information from PDFs.
                </p>
                <br/>
                <figure className='text-center'>
                <img
                    src={llama_q2}
                    alt="LLama q2 output"
                    className="my-4 mx-auto"
                    loading="lazy"
                    width="924"
                    height="214"
                />
                <figcaption>
                    Output from LLaMa3.2:1b-instruct-q2_k
                </figcaption>
                </figure>
                <figure className='text-center'>
                <img
                    src={llama_q8}
                    alt="LLama q8 output"
                    className="my-4 mx-auto"
                    loading="lazy"
                    width="696"
                    height="217"
                />
                <figcaption className='text-'>
                    Output from LLaMa3.2:1b-instruct-q8_0
                </figcaption>
                </figure>
                <br/>
                <p>
                    Comparing their outputs makes it clear that increasing quantization strength reduces accuracy: the q2_k model produces more extraction errors, misclassifications, and formatting deviations than the q8_0 model.
                </p>
            </>
        )
    }
  ]
}

export default QuantizationInLLMsContent;